import { z } from "zod";
import { Id } from "../common/ids.js";
import { Timestamp } from "../common/time.js";
import { defineRoute } from "../http/route.js";
const Ops = { surface: "operator", auth: "operator", permission: "ops:audit.read", marks: [] };
export const AuditEvent = z
    .object({
    id: Id,
    occurredAt: Timestamp,
    actorType: z.enum(["user", "operator", "system"]),
    actorId: z.string().nullable(),
    tenantIds: z.array(Id),
    entityType: z.string(),
    entityId: z.string(),
    action: z.string(),
    before: z.unknown(),
    after: z.unknown(),
    evidenceSha256: z.string().nullable(),
    requestId: z.string().nullable(),
    prevHash: z.string().nullable(),
    hash: z.string(),
})
    .meta({ id: "AuditEvent" });
export const ChainProblemKind = z
    .enum(["hash_mismatch", "missing_root", "fork", "unlinked"])
    .meta({ id: "ChainProblemKind" });
export const ChainBreak = z
    .object({ entityType: z.string(), entityId: z.string(), eventId: Id, kind: ChainProblemKind })
    .meta({ id: "ChainBreak" });
export const AuditVerificationRun = z
    .object({
    id: Id,
    startedAt: Timestamp,
    finishedAt: Timestamp.nullable().describe("Null while the run is in progress, or when it stopped"),
    entitiesChecked: z.number().int().nonnegative(),
    eventsChecked: z.number().int().nonnegative(),
    breaks: z.array(ChainBreak),
})
    .meta({ id: "AuditVerificationRun" });
const Text = z.string().trim().min(1).max(200);
export const listAuditEvents = defineRoute({
    ...Ops,
    id: "ops.audit.events.list",
    method: "GET",
    path: "/v1/ops/audit-events",
    summary: "Audit events, newest first, filtered by entity, actor, action, enterprise, and time",
    prd: ["AUD-1", "ADM-1"],
    request: {
        query: z.object({
            entityType: Text.optional(),
            entityId: Text.optional(),
            actorId: Text.optional(),
            action: Text.optional(),
            tenantId: Id.optional().describe("Events naming this enterprise"),
            from: Timestamp.optional().describe("Inclusive"),
            to: Timestamp.optional().describe("Exclusive"),
            limit: z.coerce.number().int().min(1).max(200).default(50),
            cursor: z.string().min(1).max(200).optional().describe("Opaque; pass nextCursor back"),
        }),
    },
    responses: {
        200: {
            description: "A page of events",
            schema: z.object({ items: z.array(AuditEvent), nextCursor: z.string().nullable() }),
        },
    },
    errors: ["AUTH_REQUIRED", "PERMISSION_DENIED", "VALIDATION_FAILED"],
});
export const verifyAuditChain = defineRoute({
    ...Ops,
    id: "ops.audit.chain.verify",
    method: "GET",
    path: "/v1/ops/audit-chains/{entityType}/{entityId}",
    summary: "Re-hash one entity's chain now and say whether it is intact",
    prd: ["AUD-1"],
    request: { params: z.object({ entityType: Text, entityId: Text }) },
    responses: {
        200: {
            description: "The verdict",
            schema: z
                .object({
                entityType: z.string(),
                entityId: z.string(),
                events: z.number().int().nonnegative(),
                intact: z.boolean(),
                problem: z.object({ eventId: Id, kind: ChainProblemKind }).nullable(),
            })
                .meta({ id: "AuditChainVerdict" }),
        },
    },
    errors: ["AUTH_REQUIRED", "PERMISSION_DENIED", "NOT_FOUND"],
});
export const latestAuditVerification = defineRoute({
    ...Ops,
    id: "ops.audit.verification.latest",
    method: "GET",
    path: "/v1/ops/audit-verification",
    summary: "The latest nightly verification of every chain",
    prd: ["AUD-1"],
    responses: {
        200: {
            description: "The latest run, or null before the first",
            schema: z.object({ run: AuditVerificationRun.nullable() }),
        },
    },
    errors: ["AUTH_REQUIRED", "PERMISSION_DENIED"],
});
export const auditRoutes = [listAuditEvents, verifyAuditChain, latestAuditVerification];
//# sourceMappingURL=audit.routes.js.map